import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  IsLoadingUsers,
  IsShowingActives,
  load,
  SelectCostCenters,
  SelectCostCentersUsersComplete,
  SelectFiltersUsers,
  SelectOrderedBy,
  SelectSupervisors,
  setIsLoadingUsers,
} from "../redux/reducers/usersList.reducer";
import { UsersService } from "../services/users";
import { selectMetaTimesData } from "../../TimesModule/redux/reducers/metaTimes.reducer";
import { useTimesMetadata } from "../../TimesModule/hooks/useTimesMetadata";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import {
  canSeeAllUsers,
  isSupervisor,
} from "../../SharedModule/utils/accessUtils";

export const useUsers = () => {
  useTimesMetadata();
  const dispatch = useDispatch();
  const isLoadingUsers = useSelector(IsLoadingUsers);
  const isShowingActives = useSelector(IsShowingActives);
  const selectFiltersUsers = useSelector(SelectFiltersUsers)
  const { userlist: users } = useSelector(SelectOrderedBy);
  const costCentersUsers = useSelector(SelectCostCenters);
  const costCentersUsersComplete = useSelector(SelectCostCentersUsersComplete);
  const supervisors = useSelector(SelectSupervisors);

  const { userRolesList, activeUserEmail, rolesLoaded } =
    useSelector(selectUserRoles);
  const isSupervisorInfo = isSupervisor(userRolesList);
  const isNotAdminOrViewer = !canSeeAllUsers(userRolesList);

  const MetaTimesInfo = useSelector(selectMetaTimesData);

  const loadUsers = useCallback(async () => {
    if (MetaTimesInfo.timeUsers && rolesLoaded && users.length === 0) {
      dispatch(setIsLoadingUsers(true));
      if (isSupervisorInfo && isNotAdminOrViewer) {
        const [profileInfo, costCentersInfo, costCentersOwned, supervisors] =
          await Promise.all([
            UsersService.getUsersWithProfileInfo(),
            UsersService.getCostCenters(),
            UsersService.getCostCentersOwned(activeUserEmail),
            UsersService.getSupervisors(),
          ]);
        dispatch(
          load({
            profileInfo: profileInfo,
            costCentersInfo: costCentersInfo,
            costCentersOwned: costCentersOwned,
            allUsers: MetaTimesInfo.timeUsers,
            useAllCostCenters: !isSupervisorInfo || !isNotAdminOrViewer,
            supervisors: supervisors,
          })
        );
      } else {
        const [profileInfo, costCentersInfo, supervisors] = await Promise.all([
          UsersService.getUsersWithProfileInfo(),
          UsersService.getCostCenters(),
          UsersService.getSupervisors(),
        ]);
        dispatch(
          load({
            profileInfo: profileInfo,
            costCentersInfo: costCentersInfo,
            costCentersOwned: null,
            allUsers: MetaTimesInfo.timeUsers,
            useAllCostCenters: !isSupervisorInfo || !isNotAdminOrViewer,
            supervisors: supervisors,
          })
        );
      }
    }
  }, [dispatch, MetaTimesInfo.timeUsers, rolesLoaded]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  return {
    users,
    costCentersUsers,
    costCentersUsersComplete,
    isLoadingUsers,
    isShowingActives,
    selectFiltersUsers,
    userRolesList,
    rolesLoaded,
    supervisors,
  };
};
